import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  AGE_BAND_LABEL,
  CardDiscardReasonEnum,
  CardService,
  DateUtil,
  DISCARD_REASON_LABEL,
  Funnel,
  FunnelOverview,
  FunnelOverviewLossReason,
  FunnelOverviewStage,
  PipelineSummary,
  PipelineSummaryFigure,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { AppCRMState, ParamsState } from '../state/app.state';
import { queryParamsSelector } from '../state/selectors';

export type OverviewPeriodPreset = 'currentMonth' | 'lastMonth' | 'currentQuarter';

/**
 * Funnel diagnostics (SPEC-2026-08-crm-pipeline-deals-radar — RF-21, AC-10).
 *
 * Answers "which stage do deals die in" (US-05): conversion between consecutive
 * stages as a stage-entry cohort inside the period, average time in stage, and the
 * age distribution of open deals.
 *
 * The period is the BOARD's period (SPEC-2026-10 RF-08): this component reads the same
 * filter state the columns read and never computes a range of its own, so the overview's
 * "won" and the won column's header describe the same deals. Its presets fill the board's
 * period through the filter panel, which is the one writer of that state.
 */
@Component({
  selector: 'app-funnel-overview',
  templateUrl: './funnel-overview.component.html',
  styleUrls: ['./funnel-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FunnelOverviewComponent extends AbstractComponent implements OnInit, OnChanges {

  @Input() selectedFunnel: Funnel | null = null;

  /** A preset picked here becomes the board's period; the Kanban hands it to the filter panel. */
  @Output() periodChange = new EventEmitter<{ from: Date; to: Date }>();

  overview?: FunnelOverview;

  /**
   * Open / won / lost figures for the period (RF-01, RF-02, RF-04).
   *
   * They used to sit permanently above the board. Moved in here on 2026-08-18: the CRM
   * dashboard presents the same three numbers with the same variation against the
   * preceding period, so keeping a second copy on the board meant two places to
   * disagree. Inside the diagnostic they answer the question the supervisor actually
   * opened it to ask — how the pipeline moved AND where deals die — in one reading.
   */
  summary?: PipelineSummary;

  /** The period in use, as the board's filter state carries it (`dateStart` / `dateEnd`, `yyyy-MM-dd`). */
  dateStart: string | null = null;
  dateEnd: string | null = null;

  readonly periods: Array<{ value: OverviewPeriodPreset; label: string }> = [
    { value: 'currentMonth', label: 'kanban.pipelineSummary.periodCurrentMonth' },
    { value: 'lastMonth', label: 'kanban.pipelineSummary.periodLastMonth' },
    { value: 'currentQuarter', label: 'kanban.pipelineSummary.periodCurrentQuarter' },
  ];

  constructor(
    injector: Injector,
    private readonly cardService: CardService,
    private readonly store: Store<AppCRMState>,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    // Same selector the columns read: one period for the board and the overview (RF-08).
    this.store.select(queryParamsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: ParamsState) => {
        const params = (state?.params ?? {}) as Record<string, unknown>;
        this.dateStart = this.asText(params['dateStart']);
        this.dateEnd = this.asText(params['dateEnd']);
        this.load();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedFunnel'] && !changes['selectedFunnel'].firstChange) {
      this.load();
    }
  }

  /** The preset fills the board's period; the reload comes back through the store. */
  selectPreset(preset: OverviewPeriodPreset): void {
    this.periodChange.emit(this.rangeOf(preset));
  }

  /**
   * The period actually read, as the server answered it, shown in the company's zone (RF-05);
   * a plain label until the summary arrives.
   */
  get periodLabel(): string {
    const period = this.summary?.period;
    if (!period?.start || !period?.end) return this.t('kanban.pipelineSummary.period');

    const zone = this.getTimezoneCompanyCurrentUser();
    const from = DateUtil.toFormart(period.start, 'dd/MM/yyyy', zone);
    const to = DateUtil.toFormart(period.end, 'dd/MM/yyyy', zone);
    return from === to ? from : `${from} – ${to}`;
  }

  /** A persisted filter value as text: the calendar day the panel stores, or the milliseconds an earlier build did. */
  private asText(value: unknown): string | null {
    return value === null || value === undefined || value === '' ? null : `${value}`;
  }

  private load(): void {
    if (!this.selectedFunnel?._id) return;

    this.loading = true;

    // Only the period travels with the pipeline: the overview describes the funnel, not the
    // board's other filters. The panel always sets one; without it the server falls back to the
    // company's current day.
    const params: Record<string, string> = { funnel: this.selectedFunnel._id };
    if (this.dateStart && this.dateEnd) {
      params['dateStart'] = this.dateStart;
      params['dateEnd'] = this.dateEnd;
    }

    this.cardService
      .getPipelineOverview(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (overview) => {
          this.overview = overview;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.overview = undefined;
        },
      });

    this.cardService
      .getPipelineSummary(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        // A failed summary must not blank the diagnostic: conversion and time in stage
        // are the parts nothing else in the product provides.
        next: (summary) => this.summary = summary,
        error: () => this.summary = undefined,
      });
  }

  /** The calendar range of a preset, in the user's calendar; the panel turns it into calendar days. */
  private rangeOf(preset: OverviewPeriodPreset): { from: Date; to: Date } {
    const now = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

    if (preset === 'lastMonth') {
      return {
        from: startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        to: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    }
    if (preset === 'currentQuarter') {
      const quarterStart = Math.floor(now.getMonth() / 3) * 3;
      return {
        from: startOfDay(new Date(now.getFullYear(), quarterStart, 1)),
        to: endOfDay(new Date(now.getFullYear(), quarterStart + 3, 0)),
      };
    }
    return {
      from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }

  /** A null variation means the preceding period was empty — shown as a dash (RF-04). */
  hasVariation(figure?: PipelineSummaryFigure): boolean {
    return figure?.countVariation !== null && figure?.countVariation !== undefined;
  }

  /** More lost deals is not good news, so the lost figure inverts the reading. */
  variationContext(variation: number | null | undefined, invert = false): string {
    if (variation === null || variation === undefined || variation === 0) return 'secondary';
    const positive = variation > 0;
    return (invert ? !positive : positive) ? 'success' : 'danger';
  }

  variationIcon(variation: number | null | undefined): string {
    if (variation === null || variation === undefined || variation === 0) return 'ri-subtract-line';
    return variation > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line';
  }

  /**
   * `null` conversion is NOT 0%: it means the last stage, or a stage nobody reached in
   * the period. Showing 0% there would read as "everybody dropped out" (AC-10).
   */
  hasConversion(stage: FunnelOverviewStage): boolean {
    return stage?.conversionToNext !== null && stage?.conversionToNext !== undefined;
  }

  /** Low conversion is the signal the supervisor is looking for, so it is coloured. */
  conversionContext(stage: FunnelOverviewStage): string {
    if (!this.hasConversion(stage)) return 'secondary';
    if (stage.conversionToNext >= 60) return 'success';
    if (stage.conversionToNext >= 30) return 'warning';
    return 'danger';
  }

  /**
   * Milliseconds are unreadable on screen. Days is the unit the sales cycle is
   * discussed in; under a day falls back to hours so a fast stage does not read "0".
   */
  formatDuration(ms: number | null): string {
    if (ms === null || ms === undefined) return '—';

    const hours = ms / 3_600_000;
    if (hours < 24) return this.t('kanban.funnelOverview.hours', { hours: Math.max(1, Math.round(hours)) });

    return this.t('kanban.funnelOverview.days', { days: Math.round(hours / 24) });
  }

  ageBandLabel(band: string): string {
    return AGE_BAND_LABEL[band] ?? band;
  }

  /** Share of the largest band, so the bars are comparable to each other. */
  ageBarPercent(count: number): number {
    const max = Math.max(...(this.overview?.ageDistribution ?? []).map(band => band.count), 1);
    return Math.round((count / max) * 100);
  }

  get totalOpen(): number {
    return (this.overview?.ageDistribution ?? []).reduce((total, band) => total + band.count, 0);
  }

  /** E3 RF-27: a platform discard is labelled from the catalogs; a company reason by its own name. */
  lossReasonLabel(row: FunnelOverviewLossReason): string {
    if (row.platform) return this.t(DISCARD_REASON_LABEL[row.reason as CardDiscardReasonEnum] ?? `${row.reason}`);
    return row.name ?? this.t('kanban.funnelOverview.noLossReason');
  }

  lossReasonTrack(row: FunnelOverviewLossReason): string {
    return row.reason ?? 'none';
  }

  get totalLost(): number {
    return (this.overview?.lossReasons ?? []).reduce((total, row) => total + row.count, 0);
  }
}
